import axios from 'axios';
import memoize from "memoizee";
import { getAuthCookie } from 'src/utils/cookies';
import store from 'src/store';
import { auth } from 'src/actions';

const SECOND = 1000;

const handle201 = (err) => {
  if (err.response.status === 201) {
    return err.response;
  }
  throw err;
};

const handle401 = (err) => {
  if (err.response.status === 401) {
    console.log('err.response', err.response.config);
    console.log('err.response.authorization', err.response.config.authorization);
    store.dispatch(auth.logout());
    delete err.response.config.headers.authorization;
    return axios.request(err.response.config);
  }
  throw err;
};

var axiosInstance = axios.create({
  baseURL: 'https://api.russianhackers.org',
});

const getAuthHeader = () => {
  const header = {};
  header['authorization'] = `bearer ${getAuthCookie()}`;
  return header;
};

const requestsWithAuth = {
  get: (url) => axiosInstance.get(url, { headers: { ...getAuthHeader() } }).catch(handle401).catch(handle201),
  post: (url, body) => axiosInstance.post(url, body, { headers: { ...getAuthHeader() } }).catch(handle401).catch(handle201),
  put: (url, body) => axiosInstance.put(url, body, { headers: { ...getAuthHeader() } }).catch(handle401).catch(handle201),
  delete: (url) => axiosInstance.delete(url, { headers: { ...getAuthHeader() } }).catch(handle401).catch(handle201),
  patch: (url, body) => axiosInstance.patch(url, body, { headers: { ...getAuthHeader() } }).catch(handle401).catch(handle201),
};

const requests = {
  get: (url) => axiosInstance.get(url).catch(handle401).catch(handle201),
  post: (url, body) => axiosInstance.post(url, body).catch(handle401).catch(handle201),
  put: (url, body) => axiosInstance.put(url, body).catch(handle401).catch(handle201),
  delete: (url) => axiosInstance.delete(url).catch(handle401).catch(handle201),
  patch: (url, body) => axiosInstance.patch(url, body).catch(handle401).catch(handle201),
};

const memoizedRequests = {
  checkUsername: memoize((username) => requestsWithAuth.get(`/api/user/check_username/${encodeURI(username)}`)
      .then(res => {
        if (res.data) {
          // Prefetching userId's for form validation
          memoizedRequests.getUserIdByName(username);
        }
        return res.data;
      })
      .catch(err => {
        if (err.response.status === 404) {
          return false;
        }
        throw err;
    }), { promise: true }),
  getUserIdByName: memoize((username) => requestsWithAuth.get(`/api/user/search/${username}`)
      .then(res => res.data)
      .catch(err => {
        if (err.response.status === 404) {
          return false;
        }
        throw err;
      }), { promise: true }),  
};


// TODO: add api
// https://tech.yandex.com/maps/doc/geocoder/desc/examples/geocoder_examples-docpage/#geocoder_examples__json
const yandexApiUrl = 'https://geocode-maps.yandex.ru/1.x/';
export const Yandex = {
  getGeolocationByAdress: (address) => requests.get(`${yandexApiUrl}?format=json&geocode=${address}&lang=en-US`),
};

export const Auth = {
  login: (username, password) => requests.post('/api/user/login', { username, password }),
  loginWithToken: (token) => requests.get(`/api/user/register/${token}/`),
  register: (username, password, email) => requestsWithAuth.post('/api/user/register/', { username, password, email }),
  resetPassword: (email) => requests.post('/api/user/password_reset/', { email }),
  refreshToken: (token) => requestsWithAuth.post('/api/user/refresh', { token }),
  setNewPassword: (token, password) => requestsWithAuth.post(`/api/user/password_reset/${token}/`, { password }),
  logout: () => {}
};

export const Events = {
  getEvents: (offset, limit) => requests.get(`/api/events/future/?offset=${offset}&limit=${limit}`),
  recommendEvent: (submitForm) => requestsWithAuth.post('/api/events/', submitForm),
  getEventById: (eventId) => requestsWithAuth.get(`/api/events/id/${eventId}/`),
  getEventPartners: (eventId) => requestsWithAuth.get(`/api/events/id/${eventId}/partners`),
  getEventOrganizers: (eventId) => requestsWithAuth.get(`/api/events/id/${eventId}/organizer`),
};

export const User = {
  getUserProfile: () => Promise.all([
    requestsWithAuth.get('/api/user/'),
    User.refetchUserEvents(),
    User.refetchUserWallet()
  ]),
  enrollToEvent: (eventId) => requestsWithAuth.put(`/api/events/user/${eventId}`, { })
    .catch((err) => {
      if (err && err.response && err.response.data && err.response.data) {
        throw err.response.data;
      } else {
        throw err;
      }
    }),
  refetchUserEvents: memoize(() => requestsWithAuth.get('/api/events/user/'), { maxAge: 1 * SECOND }),
  refetchUserWallet: memoize(() => requestsWithAuth.get('/api/user/wallet/'), { maxAge: 1 * SECOND }),
  unEnrollFromEvent: (eventId) => requestsWithAuth.delete(`/api/events/user/${eventId}`),
  checkUserName: (username) => memoizedRequests.checkUsername(username),
  getUserIdByName: (username) => memoizedRequests.getUserIdByName(username),
  getUserTokens: () => requestsWithAuth.get('/api/user/wallet/'),
  updateUser: {
    updateSkillsAndRoles: ({ skills = [], roles = [] }) => requestsWithAuth.patch('/api/user/', { skills, roles }),
    updateSocialNetworks: (socialNetworks) => requestsWithAuth.patch('/api/user/', socialNetworks),
  }
};

export const Project = {
  createProject: (projectForm) => requestsWithAuth.post('/api/project/', projectForm),
  refetchUserProjects: memoize(() => requestsWithAuth.get('/api/project/all/'), { maxAge: 1 * SECOND }),
  getProjectById: (projectId) => requestsWithAuth.get(`/api/project/${projectId}`),
  updateProject: (projectId, projectForm) => requestsWithAuth.patch(`/api/project/${projectId}`, projectForm),
};

export const Reviews = {
  createReview: (review) => requestsWithAuth.post('/api/review/', review),
  refetchUserReview: memoize(() => requestsWithAuth.get('/api/user/review/'), { maxAge: 1 * SECOND }),
  createNews: (news) => requestsWithAuth.post('/api/news/', { text: news }),
}



// WEBPACK FOOTER //
// ./src/api/index.js